import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Info } from 'lucide-react';
import { useForm, FormProvider } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useSaveAuthConfig } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type {
  IToolAuthConfig,
  IToolDetails,
  ToolAuthLocation,
  ToolAuthType,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { getToolApiConfig, getToolAuthConfig, patchToolAuthConfigInCache } from './tool-detail-utils';
import { ToolAuthCard } from './tool-auth-card';
import { ToolConnectionCard } from './tool-connection-card';

type HeaderField = { id: string; key: string; value: string };

const EMPTY_HEADER_ROW: HeaderField = { id: '1', key: '', value: '' };

const buildHeadersObject = (headers: HeaderField[]): Record<string, string> =>
  headers.reduce(
    (acc, header) => {
      if (header.key && header.value) {
        acc[header.key] = header.value;
      }
      return acc;
    },
    {} as Record<string, string>
  );

const toHeaderFields = (headers?: Record<string, string>): HeaderField[] => {
  if (!headers || Object.keys(headers).length === 0) {
    return [EMPTY_HEADER_ROW];
  }

  return Object.entries(headers).map(([key, value], index) => ({
    id: (index + 1).toString(),
    key,
    value,
  }));
};

type AuthFormValues = {
  authType: ToolAuthType;
  authLocation: ToolAuthLocation;
  authKeyName: string;
  authKeyPrefix: string;
  apiToken: string;
  username: string;
  password: string;
  clientId: string;
  clientSecret: string;
  tokenUrl: string;
  scopes: string;
  grantType: 'authorization_code' | 'client_credentials' | 'refresh_token';
  customHeaders: HeaderField[];
  oauth2Headers: HeaderField[];
  // Auto (per-user, token-only) fields
  autoSubType: 'password' | 'oidc' | 'host';
  loginUrl: string;
  loginMethod: string;
  usernameField: string;
  passwordField: string;
  tokenJsonPath: string;
  expiresJsonPath: string;
  refreshUrl: string;
  refreshTokenJsonPath: string;
  refreshRequestField: string;
  authorizationEndpoint: string;
  // Static extra fields for the login / refresh request bodies (e.g. grant_type),
  // static headers for the login request (e.g. origin, x-blocks-key), and cookie-mode.
  loginExtraBody: HeaderField[];
  refreshExtraBody: HeaderField[];
  loginHeaders: HeaderField[];
  // OIDC code->token exchange body (placeholders {code} {state} {client_id} … ). Empty = standard OIDC.
  tokenRequestBody: HeaderField[];
  // Optional brokered authorize URL (e.g. Blocks GetSocialLogInEndPoint).
  authorizeUrlEndpoint: string;
  authorizeUrlBody: HeaderField[];
  authorizeUrlJsonPath: string;
  tokenAsCookie: boolean;
  tokenCookieName: string;
  acceptHostToken: boolean;
};

export function ToolAuthentication({ tool }: Readonly<{ tool: IToolDetails }>) {
  const { t } = useTranslation();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canEdit = useIsOwner(tool);
  const apiConfig = getToolApiConfig(tool);
  const authConfig = getToolAuthConfig(tool);
  const savedAuto = (authConfig as Record<string, any>) || {};
  // No-auth tools (e.g. public APIs) can't be created from this UI, but they can be
  // seeded/imported — so render an honest read-only state instead of the Auto card.
  const isSavedNoAuth = (savedAuto.AuthType || savedAuto.auth_type) === 'none';
  const isSavedAuto = !isSavedNoAuth;
  const savedSubType = savedAuto.AutoSubType || savedAuto.auto_sub_type;
  const savedAutoSubType: 'password' | 'oidc' = savedSubType === 'oidc' ? 'oidc' : 'password';
  const isSavedHost = savedSubType === 'host';
  const { mutateAsync: saveAuthConfig, isPending } = useSaveAuthConfig();
  const [customHeaders, setCustomHeaders] = useState<HeaderField[]>([EMPTY_HEADER_ROW]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Initial client secret from API — omit from save payload when unchanged. */
  const initialClientSecretRef = useRef<string>('');

  const form = useForm<AuthFormValues>({
    defaultValues: {
      authType: 'auto',
      authLocation: 'header',
      authKeyName: '',
      authKeyPrefix: '',
      apiToken: '',
      username: '',
      password: '',
      clientId: '',
      clientSecret: '',
      tokenUrl: '',
      scopes: '',
      grantType: 'client_credentials',
      customHeaders: [],
      oauth2Headers: [{ id: '1', key: '', value: '' }],
      autoSubType: 'password',
      loginUrl: '',
      loginMethod: 'POST',
      usernameField: '',
      passwordField: '',
      tokenJsonPath: '',
      expiresJsonPath: '',
      refreshUrl: '',
      refreshTokenJsonPath: '',
      refreshRequestField: '',
      authorizationEndpoint: '',
      loginExtraBody: [],
      refreshExtraBody: [],
      loginHeaders: [],
      tokenRequestBody: [],
      authorizeUrlEndpoint: '',
      authorizeUrlBody: [],
      authorizeUrlJsonPath: '',
      tokenAsCookie: false,
      tokenCookieName: '',
      acceptHostToken: false,
    },
  });

  useEffect(() => {
    const formValues: Partial<AuthFormValues> = { authType: 'auto' };

    if (authConfig) {
      const auto = authConfig as Record<string, any>;
      const resolvedAuthType = authConfig.AuthType || auto.auth_type;

      // Tool details always uses auto auth — load saved auto fields when present.
      if (resolvedAuthType === 'auto') {
        const saved = auto.AutoSubType || auto.auto_sub_type || 'password';
        formValues.autoSubType = saved === 'oidc' || saved === 'host' ? saved : 'password';
        formValues.authKeyName = auto.AuthKeyName || auto.auth_key_name || 'Authorization';
        formValues.authKeyPrefix = auto.AuthKeyPrefix || auto.auth_key_prefix || 'Bearer';
        formValues.loginUrl = auto.LoginUrl || auto.login_url || '';
        formValues.loginMethod = auto.LoginMethod || auto.login_method || 'POST';
        formValues.usernameField = auto.UsernameField || auto.username_field || '';
        formValues.passwordField = auto.PasswordField || auto.password_field || '';
        formValues.tokenJsonPath = auto.TokenJsonPath || auto.token_json_path || '';
        formValues.expiresJsonPath = auto.ExpiresJsonPath || auto.expires_json_path || '';
        formValues.refreshUrl = auto.RefreshUrl || auto.refresh_url || '';
        formValues.refreshTokenJsonPath = auto.RefreshTokenJsonPath || auto.refresh_token_json_path || '';
        formValues.refreshRequestField = auto.RefreshRequestField || auto.refresh_request_field || '';
        formValues.loginExtraBody = toHeaderFields(auto.LoginExtraBody || auto.login_extra_body);
        formValues.refreshExtraBody = toHeaderFields(auto.RefreshExtraBody || auto.refresh_extra_body);
        formValues.loginHeaders = toHeaderFields(auto.CustomHeaders || auto.custom_headers);
        formValues.tokenRequestBody = toHeaderFields(auto.TokenRequestBody || auto.token_request_body);
        formValues.authorizeUrlEndpoint = auto.AuthorizeUrlEndpoint || auto.authorize_url_endpoint || '';
        formValues.authorizeUrlBody = toHeaderFields(auto.AuthorizeUrlBody || auto.authorize_url_body);
        formValues.authorizeUrlJsonPath = auto.AuthorizeUrlJsonPath || auto.authorize_url_json_path || '';
        formValues.tokenAsCookie = !!(auto.TokenAsCookie ?? auto.token_as_cookie);
        formValues.tokenCookieName = auto.TokenCookieName || auto.token_cookie_name || '';
        formValues.acceptHostToken = !!(auto.AcceptHostToken || auto.accept_host_token);
        const oidc = auto.OAuth2Config || auto.oauth2_config || {};
        formValues.authorizationEndpoint =
          oidc.AuthorizationEndpoint || oidc.authorization_endpoint || '';
        formValues.tokenUrl = oidc.TokenURL || oidc.token_url || '';
        formValues.clientId = oidc.ClientID || oidc.client_id || '';
        const clientSecret = oidc.ClientSecret || oidc.client_secret || '';
        formValues.clientSecret = clientSecret;
        initialClientSecretRef.current = clientSecret;
        formValues.scopes = Array.isArray(oidc.Scopes)
          ? oidc.Scopes.join(', ')
          : Array.isArray(oidc.scopes)
            ? oidc.scopes.join(', ')
            : '';
      }
    }

    form.reset({ ...form.getValues(), ...formValues });
    setCustomHeaders(toHeaderFields(apiConfig?.Headers));
  }, [apiConfig?.Headers, authConfig, form]);

  const addHeaderField = () => {
    setCustomHeaders([...customHeaders, { id: Date.now().toString(), key: '', value: '' }]);
  };

  const removeHeaderField = (id: string) => {
    setCustomHeaders(customHeaders.filter((field) => field.id !== id));
  };

  const updateHeaderField = (id: string, field: 'key' | 'value', newValue: string) => {
    setCustomHeaders(customHeaders.map((item) => (item.id === id ? { ...item, [field]: newValue } : item)));
  };

  const validate = () => {
    const values = form.getValues();
    const newErrors: Record<string, string> = {};

    if (values.authType === 'bearer') {
      if (!values.authKeyName) newErrors.authKeyName = t('KEY_REQUIRED') || 'Key is required';
      if (!values.apiToken) newErrors.apiToken = t('SECRET_REQUIRED') || 'Secret is required';
      if (!values.authKeyPrefix) newErrors.authKeyPrefix = t('PREFIX_REQUIRED') || 'Prefix is required';
    } else if (values.authType === 'basic') {
      if (!values.username) newErrors.username = t('USERNAME_REQUIRED') || 'Username is required';
      if (!values.password) newErrors.password = t('PASSWORD_REQUIRED') || 'Password is required';
      if (!values.authKeyPrefix) newErrors.authKeyPrefix = t('PREFIX_REQUIRED') || 'Prefix is required';
    } else if (values.authType === 'api_token') {
      if (!values.authKeyName) newErrors.authKeyName = t('KEY_REQUIRED') || 'Key is required';
      if (!values.apiToken) newErrors.apiToken = t('SECRET_REQUIRED') || 'Secret is required';
      if (values.authLocation !== 'query' && !values.authKeyPrefix) {
        newErrors.authKeyPrefix = t('PREFIX_REQUIRED') || 'Prefix is required';
      }
    } else if (values.authType === 'oauth2') {
      if (!values.clientId) newErrors.clientId = t('CLIENT_ID_REQUIRED') || 'Client ID is required';
      if (!values.clientSecret) newErrors.clientSecret = t('CLIENT_SECRET_REQUIRED') || 'Client Secret is required';
      if (!values.tokenUrl) newErrors.tokenUrl = t('TOKEN_URL_REQUIRED') || 'Token URL is required';
    } else if (values.authType === 'auto') {
      if (values.autoSubType === 'oidc') {
        if (!values.authorizationEndpoint)
          newErrors.authorizationEndpoint = t('AUTHORIZATION_ENDPOINT_REQUIRED') || 'Authorization endpoint is required';
        if (!values.tokenUrl) newErrors.tokenUrl = t('TOKEN_URL_REQUIRED') || 'Token endpoint is required';
        if (!values.clientId) newErrors.clientId = t('CLIENT_ID_REQUIRED') || 'Client ID is required';
        if (!values.clientSecret) newErrors.clientSecret = t('CLIENT_SECRET_REQUIRED') || 'Client Secret is required';
      } else if (values.autoSubType === 'password' && !values.loginUrl) {
        // Host session needs nothing beyond the shared injection fields.
        newErrors.loginUrl = t('LOGIN_URL_REQUIRED') || 'Login URL is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!canEdit) return;
    if (!validate()) {
      toast({
        variant: 'destructive',
        title: t('VALIDATION_ERROR') || 'Validation Error',
        description: t('FILL_ALL_REQUIRED_FIELDS') || 'Please fill in all required fields',
      });
      return;
    }

    if (!projectKey) return;

    try {
      const values = form.getValues();
      const headersObject = buildHeadersObject(customHeaders);
      const authCustomHeaders = buildHeadersObject(values.oauth2Headers || []);

      const authConfig: Record<string, any> = {
        AuthType: values.authType,
      };

      if (values.authType === 'auto') {
        // v2 per-user (token-only) config — backend expects snake_case keys.
        authConfig.auth_type = 'auto';
        authConfig.auto_sub_type = values.autoSubType;
        authConfig.auth_key_name = values.authKeyName || 'Authorization';
        authConfig.auth_key_prefix = values.authKeyPrefix || 'Bearer';

        // Static headers for the login request (e.g. origin, x-blocks-key); also ride API calls.
        authConfig.custom_headers = buildHeadersObject(values.loginHeaders || []);

        if (values.autoSubType === 'host') {
          // The embedding site's token is the credential; the backend implies
          // accept_host_token and there is nothing to mint or refresh.
          authConfig.token_as_cookie = !!values.tokenAsCookie;
          if (values.tokenCookieName) authConfig.token_cookie_name = values.tokenCookieName;
        } else if (values.autoSubType === 'password') {
          authConfig.login_url = values.loginUrl;
          authConfig.login_method = values.loginMethod || 'POST';
          if (values.usernameField) authConfig.username_field = values.usernameField;
          if (values.passwordField) authConfig.password_field = values.passwordField;
          if (values.tokenJsonPath) authConfig.token_json_path = values.tokenJsonPath;
          if (values.expiresJsonPath) authConfig.expires_json_path = values.expiresJsonPath;
          authConfig.login_extra_body = buildHeadersObject(values.loginExtraBody || []);
          authConfig.token_as_cookie = !!values.tokenAsCookie;
          if (values.tokenCookieName) authConfig.token_cookie_name = values.tokenCookieName;
        } else {
          authConfig.oauth2_config = {
            authorization_endpoint: values.authorizationEndpoint,
            token_url: values.tokenUrl,
            client_id: values.clientId,
            client_secret: values.clientSecret,
            scopes: values.scopes ? values.scopes.split(',').map((s) => s.trim()).filter(Boolean) : [],
            grant_type: 'authorization_code',
          };
          // Configurable code->token exchange body (empty = standard OIDC on the backend).
          authConfig.token_request_body = buildHeadersObject(values.tokenRequestBody || []);
          // Optional brokered authorize URL (broker builds the URL + owns state, e.g. Blocks).
          if (values.authorizeUrlEndpoint) authConfig.authorize_url_endpoint = values.authorizeUrlEndpoint;
          authConfig.authorize_url_body = buildHeadersObject(values.authorizeUrlBody || []);
          if (values.authorizeUrlJsonPath) authConfig.authorize_url_json_path = values.authorizeUrlJsonPath;
          // Which response field carries the bearer — standard OIDC returns
          // access_token, but some providers put it elsewhere.
          if (values.tokenJsonPath) authConfig.token_json_path = values.tokenJsonPath;
        }

        if (values.autoSubType !== 'host') {
          authConfig.accept_host_token = !!values.acceptHostToken;
          if (values.refreshUrl) authConfig.refresh_url = values.refreshUrl;
          if (values.refreshTokenJsonPath) authConfig.refresh_token_json_path = values.refreshTokenJsonPath;
          if (values.refreshRequestField) authConfig.refresh_request_field = values.refreshRequestField;
          authConfig.refresh_extra_body = buildHeadersObject(values.refreshExtraBody || []);
        }
      } else if (values.authType !== 'none') {
        if (values.authLocation) authConfig.AuthKeyLocation = values.authLocation;
        if (values.authKeyName) authConfig.AuthKeyName = values.authKeyName;
        if (values.authKeyPrefix) authConfig.AuthKeyPrefix = values.authKeyPrefix;
        if (values.apiToken) authConfig.APIToken = values.apiToken;
        if (values.username) authConfig.Username = values.username;
        if (values.password) authConfig.Password = values.password;

        if (values.authType === 'oauth2') {
          authConfig.OAuth2Config = {
            ClientID: values.clientId,
            ClientSecret: values.clientSecret,
            TokenURL: values.tokenUrl,
            Scopes: values.scopes ? values.scopes.split(',').map((s) => s.trim()) : [],
            GrantType: values.grantType,
          };
        }

        if (values.authType === 'oauth2' || values.authType === 'api_token') {
          authConfig.CustomHeaders = authCustomHeaders;
        } else {
          authConfig.CustomHeaders = {};
        }
      }

      // Omit unchanged client_secret from the API payload (keep it in authConfig for local cache).
      const apiAuthConfig = { ...authConfig };
      if (
        values.authType === 'auto' &&
        values.autoSubType === 'oidc' &&
        values.clientSecret === initialClientSecretRef.current
      ) {
        apiAuthConfig.oauth2_config = { ...authConfig.oauth2_config };
        delete apiAuthConfig.oauth2_config.client_secret;
      } else if (
        values.authType === 'oauth2' &&
        values.clientSecret === initialClientSecretRef.current
      ) {
        apiAuthConfig.OAuth2Config = { ...authConfig.OAuth2Config };
        delete apiAuthConfig.OAuth2Config.ClientSecret;
      }

      const response = await saveAuthConfig({
        toolId: tool.id,
        projectKey,
        payload: {
          auth_config: apiAuthConfig,
          headers: headersObject,
          project_key: projectKey,
          is_open_tool: tool.is_open_tool,
        },
      });

      if (!response.is_success) {
        throw new Error(response.message || response.detail || t('FAILED_TO_SAVE_AUTHENTICATION') || 'Failed to save authentication');
      }

      patchToolAuthConfigInCache(
        queryClient,
        tool.id,
        projectKey,
        authConfig as IToolAuthConfig,
        headersObject
      );
      // Keep baseline in sync so a subsequent save without edits still omits the secret.
      initialClientSecretRef.current = values.clientSecret;
      await queryClient.invalidateQueries({ queryKey: ['tools', 'detail', tool.id, projectKey] });

      toast({
        variant: 'success',
        title: t('AUTHENTICATION_SAVED'),
        description: t('AUTHENTICATION_SAVED_SUCCESSFULLY'),
      });
    } catch (error) {
      let errorMessage =
        t('ERROR_SAVING_AUTHENTICATION') || 'An error occurred while saving authentication configuration';

      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { data?: { detail?: string | Array<{ msg: string }> } } }).response;

        if (response?.data?.detail) {
          if (typeof response.data.detail === 'string') {
            errorMessage = response.data.detail;
          } else if (Array.isArray(response.data.detail)) {
            errorMessage = response.data.detail.map((err) => err.msg).join(', ');
          }
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      console.error('Save auth config error:', error);
      toast({
        variant: 'destructive',
        title: t('SAVE_FAILED') || 'Save failed',
        description: errorMessage,
      });
    }
  };

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-6">
        <Card>
          <CardContent className="p-6">
            {isSavedNoAuth ? (
              <p className="text-sm text-medium-emphasis">
                This tool requires no authentication.
              </p>
            ) : (
              <ToolAuthCard
                errors={errors}
                showCard={false}
                canEdit={canEdit}
                authType="auto"
                showAuthTypeSelector={false}
              />
            )}
          </CardContent>
        </Card>

        {/* Host session has no connect flow — the embedding site owns the credential. */}
        {isSavedAuto && !isSavedHost && (
          <ToolConnectionCard
            toolId={tool.id}
            toolName={tool.name}
            subType={savedAutoSubType}
          />
        )}

        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium">{t('HEADERS') || 'Headers'}</h3>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {t('AUTH_HEADER_FIELDS_TOOLTIP') ||
                          'Set the Header fields and values shared by all APIs within the tool here.'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('CUSTOM_API_HEADERS_DESCRIPTION') ||
                  'Define custom headers to be included in the API requests.'}
              </p>

              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <Label className="text-sm font-medium">{t('KEY') || 'Key'}</Label>
                  <Label className="text-sm font-medium">{t('VALUE') || 'Value'}</Label>
                </div>

                {customHeaders.map((field) => (
                  <div key={field.id} className="grid grid-cols-[1fr_1fr_auto] items-center gap-4">
                    <Input
                      placeholder={t('HEADER_KEY') || 'Header Key'}
                      value={field.key}
                      disabled={!canEdit}
                      onChange={(e) => updateHeaderField(field.id, 'key', e.target.value)}
                    />
                    <Input
                      placeholder={t('ENTER_VALUE') || 'Enter value'}
                      value={field.value}
                      disabled={!canEdit}
                      onChange={(e) => updateHeaderField(field.id, 'value', e.target.value)}
                    />
                    {customHeaders.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={!canEdit}
                        onClick={() => removeHeaderField(field.id)}
                        className="h-10 w-10 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canEdit}
                  onClick={addHeaderField}
                  className="w-fit gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {t('ADD') || 'Add'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {canEdit ? (
          <div className="flex justify-start">
            <Button onClick={handleSave} disabled={isPending}>
              {isPending ? t('SAVING') || 'Saving...' : t('SAVE') || 'Save'}
            </Button>
          </div>
        ) : null}
      </div>
    </FormProvider>
  );
}
